import { useEffect, useState } from 'react'

import cn from 'classnames'

import MilesWidget from '@src/components/MilesWidget'
import ChevronDownIcon from '@src/components/icons/ChevronDown'
import FuelIcon from '@src/components/icons/Fuel'
import InfoIcon from '@src/components/icons/Info'
import { isFeatureEnabled } from '@src/utils/environment'

import { DetailedRows } from './DetailedRows'
import { PriceInfo } from './PriceInfo'
import { IGasFeePanelProps } from './types'

export { DetailedRows } from './DetailedRows'
export { PriceInfo } from './PriceInfo'
export type { IDetailedRowsProps, IGasFeePanelProps } from './types'

export function GasFeePanel({
    isGasFeeExpanded,
    gasFeeString,
    onToggleGasFee,
    detailedRowsProps,
    isPriceFlipped,
    setIsPriceFlipped,
    sellData,
    buyData,
    isWrapOrUnwrapOnly,
    isBuyOutputLoading,
    isQuoteLoading,
}: IGasFeePanelProps) {
    const [showTooltip, setShowTooltip] = useState(false)
    const isTestnet = isFeatureEnabled('USE_TESTNETS_ONLY')

    // Add animation styles
    useEffect(() => {
        const style = document.createElement('style')
        style.textContent = `
            @keyframes slideIn {
                from { opacity: 0; transform: translateY(-8px); }
                to { opacity: 1; transform: translateY(0); }
            }
            .animate-slideIn {
                animation: slideIn 0.3s ease-out forwards;
            }
        `
        document.head.appendChild(style)

        return () => {
            if (document.head.contains(style)) {
                document.head.removeChild(style)
            }
        }
    }, [])

    return (
        <div className='animate-slideIn flex flex-col rounded-md py-4 text-sm font-medium text-mach-sub-text opacity-0'>
            <div className='flex w-full items-center justify-between'>
                <PriceInfo
                    isPriceFlipped={isPriceFlipped}
                    setIsPriceFlipped={setIsPriceFlipped}
                    sellData={sellData}
                    buyData={buyData}
                    isWrapOrUnwrapOnly={isWrapOrUnwrapOnly}
                    isBuyOutputLoading={isBuyOutputLoading}
                    isQuoteLoading={isQuoteLoading}
                />

                <div className='flex items-center gap-3'>
                    {isTestnet && (
                        <div className='relative'>
                            <div
                                className='cursor-pointer'
                                onMouseEnter={() => setShowTooltip(true)}
                                onMouseLeave={() => setShowTooltip(false)}
                            >
                                <InfoIcon className='h-4 w-4 text-white' />
                            </div>
                            {showTooltip && (
                                <div className='absolute bottom-full right-0 mb-2 w-48 rounded-md bg-mach-modal-background p-2 text-xs text-white shadow-lg'>
                                    Testnet gas estimations may be significantly overestimated.
                                </div>
                            )}
                        </div>
                    )}
                    <div className='flex cursor-pointer items-center gap-2 hover:opacity-80' onClick={onToggleGasFee}>
                        <div
                            className={cn(
                                'flex items-center gap-1 rounded-md bg-mach-gray px-1.5 py-1 transition-all duration-300',
                                isGasFeeExpanded ? 'opacity-0' : 'opacity-100'
                            )}
                        >
                            <FuelIcon />
                            <span className='max-w-[120px] truncate text-sm !leading-5 text-white md:max-w-none md:overflow-visible md:whitespace-nowrap'>
                                {gasFeeString}
                            </span>
                        </div>

                        <div className='flex items-center gap-1 rounded-md bg-mach-gray p-1.5'>
                            <ChevronDownIcon
                                className={cn(
                                    'h-4 w-4 text-white transition-all',
                                    isGasFeeExpanded ? 'rotate-180' : ''
                                )}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Expandable content */}
            <div
                className={cn(
                    'flex flex-col space-y-2 overflow-hidden transition-all duration-500 ease-in-out',
                    isGasFeeExpanded ? 'mt-2 max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                )}
            >
                <div
                    className={cn(
                        'transform transition-all duration-500 ease-in-out',
                        isGasFeeExpanded ? 'translate-y-0 opacity-100' : '-translate-y-4 opacity-0'
                    )}
                >
                    <DetailedRows {...detailedRowsProps} />
                </div>
            </div>
        </div>
    )
}
